import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box, DialogContent, DialogActions, Button, TextField, MenuItem, Stack } from "@mui/material";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";

const MAIN_URL = import.meta.env.VITE_MAIN_URL;

const ACCESS_KEYS = [
  { value: "ICA", labelKey: "ePortCloudLicenseLineType_7" },
  { value: "PERMITS", labelKey: "ePortCloudLicenseLineType_8" },
];

export default function HelpCategoryEditDialog({ category, onSaved }) {
  const api = useApi();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState({
    description: category?.description ?? "",
    titleTranslationKey: category?.titleTranslationKey ?? "",
    reference: category?.reference ?? "",
    iconName: category?.iconName ?? "",
    accessKey: category?.accessKey ?? "",
  });
  const [submitting, setSubmitting] = useState(false);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const canSave =
    !!form.description.trim() &&
    !!form.reference.trim() &&
    !!form.titleTranslationKey.trim() &&
    !!form.accessKey &&
    !submitting;

  async function handleSave() {
    setSubmitting(true);
    const response = await api.fetch(
      `${MAIN_URL}help/admin/categories/${encodeURIComponent(category.helpCategoryId)}`,
      {
        description: form.description,
        titleTranslationKey: form.titleTranslationKey,
        reference: form.reference,
        iconName: form.iconName,
        accessKey: form.accessKey,
      },
      "PUT",
    );
    setSubmitting(false);
    if (response && response.isSuccessful) {
      enqueueSnackbar(Translate.get("HelpCategoryUpdated"), { variant: "success" });
      if (onSaved) {
        onSaved();
      }
      navigate(-1);
    }
  }

  return (
    <>
      <DialogContent>
        <Box sx={{ minWidth: 480, pt: 2 }}>
          <Stack spacing={3}>
            <TextField
              label={Translate.get("HelpArticleDescriptionInternal")}
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              inputProps={{ maxLength: 100 }}
              fullWidth
              required
            />
            <TextField
              label={Translate.get("TitleTranslationKey")}
              value={form.titleTranslationKey}
              onChange={(e) => updateField("titleTranslationKey", e.target.value)}
              inputProps={{ maxLength: 100 }}
              fullWidth
              required
            />
            <TextField
              label={Translate.get("HelpCategoryReference")}
              value={form.reference}
              onChange={(e) => updateField("reference", e.target.value)}
              inputProps={{ maxLength: 500 }}
              fullWidth
              required
            />
            <TextField
              label={Translate.get("HelpArticleIconName")}
              value={form.iconName}
              onChange={(e) => updateField("iconName", e.target.value)}
              inputProps={{ maxLength: 50 }}
              fullWidth
            />
            <TextField
              select
              label={Translate.get("EPortProfile")}
              value={form.accessKey}
              onChange={(e) => updateField("accessKey", e.target.value)}
              fullWidth
              required
            >
              {ACCESS_KEYS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {Translate.get(option.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigate(-1)}>{Translate.get("Cancel")}</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {Translate.get("Save")}
        </Button>
      </DialogActions>
    </>
  );
}
